import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card } from "../../../../components/ui/Card";
import { apiBodyMessage } from "../../../../utils/apiErrorMessage";
import { showToast } from "../../../../utils/showToast";
import { linkOperatorMercadoLibre } from "../../../../api/integrations";

/**
 * Callback del OAuth de MercadoLibre para el OPERADOR logístico.
 *
 * MercadoLibre redirige siempre al redirect_uri registrado (el del comercio,
 * /home/integrations). ModernIntegrations detecta state=operator y desvía hacia
 * aquí conservando ?code=. Esta página intercambia el code contra el backend, que
 * guarda la integración en logisticscompanies.integrations[] (operador resuelto
 * desde la sesión), y vuelve a /admin/settings?tab=integrations.
 *
 * Vive bajo /admin/* (adminOnly ⇒ RequireAdmin) para que el operador no sea
 * rebotado y el ámbito sea el correcto (no la vista de comercio).
 */
export default function MercadoLibreOperatorCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState("processing");
  // Motivo real del fallo, cuando el backend (o MercadoLibre) dio uno usable.
  const [errorDetail, setErrorDetail] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const oauthError = params.get("error");

    const finish = (ok, detail = null) => {
      if (cancelled) return;
      setStatus(ok ? "success" : "error");
      setErrorDetail(ok ? null : detail);
      // Con motivo NO se avisa por toast acá: cuando aterriza la navegación lo
      // emite IntegrationsTab con el detalle que viaja en el state del router.
      // Hacerlo en las dos pantallas daría dos toasts rojos idénticos, porque
      // ambas comparten el mismo ToastContainer. La tarjeta (role="alert") ya
      // muestra el motivo mientras tanto.
      if (ok) {
        showToast(t("Cuenta de MercadoLibre vinculada correctamente"), true);
      } else if (!detail) {
        showToast(t("No se pudo vincular MercadoLibre"), false);
      }
      setTimeout(() => {
        if (cancelled) return;
        // El detalle viaja por el state del router y NUNCA por la query: un
        // texto libre en la URL es inyectable (basta un enlace preparado).
        navigate(
          `/admin/settings?tab=integrations&meli=${ok ? "ok" : "error"}`,
          { replace: true, state: detail ? { meliError: detail } : undefined },
        );
      }, 1200);
    };

    // Sin code (o con un ?error= de la query) no hay nada que intercambiar ni
    // motivo que mostrar: queda el texto genérico. No se lee el
    // error_description de la URL: es texto arbitrario de un enlace preparado.
    if (oauthError || !code) {
      finish(false, null);
      return () => {
        cancelled = true;
      };
    }

    linkOperatorMercadoLibre(code)
      .then((res) => {
        const ok = !!res && res.ok === true && !res.error;
        // El wrapper de la API devuelve { error: "Error desconocido" } como
        // STRING cuando la petición no llegó al backend (red): eso no es un
        // motivo del servidor, así que no se muestra. Solo un cuerpo de error
        // (objeto) trae el texto real del backend.
        const detail =
          !ok && res && res.error && typeof res.error === "object"
            ? apiBodyMessage(res.error)
            : null;
        finish(ok, detail);
      })
      .catch(() => finish(false, null));

    return () => {
      cancelled = true;
    };
  }, [navigate, t]);

  return (
    <div className="flex justify-center items-center min-h-[60vh] px-4">
      <Card className="w-full max-w-[420px] text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-brand-500 animate-spin" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">
              {t("Procesando vinculación con MercadoLibre…")}
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-status-success" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">
              {t("Cuenta de MercadoLibre vinculada. Redirigiendo…")}
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-status-error" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">
              {t("No se pudo completar la vinculación. Redirigiendo…")}
            </p>
            {errorDetail && (
              <p className="mt-2 mb-0 text-sm text-status-error" role="alert">
                {errorDetail}
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
